import React, { useEffect, useRef, useMemo } from "react";
import { Application, Graphics, Container, FederatedPointerEvent, Text, TextStyle, Sprite, Texture, Assets } from "pixi.js";
import { HexTile, Biome, BUILDING_CONFIGS } from "@ironpact/shared";

const HEX_SIZE = 24;

const BIOME_COLORS: Record<Biome, [number, number, number]> = {
  ocean:     [6, 10, 20],
  coast:     [14, 28, 48],
  desert:    [110, 88, 45],
  plains:    [48, 65, 38],
  grassland: [42, 75, 35],
  forest:    [25, 65, 30],
  mountain:  [70, 65, 72],
  tundra:    [65, 78, 88],
  swamp:     [30, 50, 35],
  volcanic:  [75, 28, 15],
};

function hexColor(q: number, r: number, biome: Biome, elevation: number, moisture: number): number {
  const base = BIOME_COLORS[biome] || [80, 80, 80];
  const hash = ((q * 374761393 + r * 668265263) ^ (q * 1274126177)) >>> 0;
  const variation = ((hash % 16) - 8);
  const elevBright = (elevation - 0.5) * 15;
  const moistGreen = (moisture - 0.5) * 8;
  const red   = Math.max(0, Math.min(255, base[0] + variation + elevBright));
  const green = Math.max(0, Math.min(255, base[1] + variation * 0.7 + moistGreen));
  const blue  = Math.max(0, Math.min(255, base[2] + variation * 0.5));
  return (Math.round(red) << 16) | (Math.round(green) << 8) | Math.round(blue);
}

const RESOURCE_INDICATORS: Record<string, { color: number; label: string; bg: number; glow: number }> = {
  steel:      { color: 0xddeeff, label: "Fe", bg: 0x667788, glow: 0x8899bb },
  fuel:       { color: 0xffcc66, label: "Gl", bg: 0xcc6600, glow: 0xff8800 },
  composites: { color: 0xddbb77, label: "Cp", bg: 0x8b6914, glow: 0xaa8833 },
  rareEarth:  { color: 0xdd88ff, label: "RE", bg: 0x8833cc, glow: 0xaa55ee },
  uranium:    { color: 0x66ffaa, label: "U",  bg: 0x117755, glow: 0x00ff88 },
};

const RESOURCE_SPRITE_MAP: Record<string, string> = {
  steel: "steel", fuel: "fuel", composites: "composites",
  rareEarth: "rareEarth", uranium: "uranium",
};


// Building sprite textures
const BUILDING_SPRITE_TYPES = [
  "mine", "oilDerrick", "steelMill", "electronicsFactory", "munitionsFactory",
  "uraniumEnrichment", "rareEarthExtractor", "compositesLab", "powerPlant",
  "barracks", "missileSilo", "antiMissileBattery", "radarStation", "bunker",
  "spyAcademy", "capital", "marketplace"
];
const BUILDING_SPRITE_MAP: Record<string, string> = {
  mine: "mine",
  oilWell: "oilDerrick",
  steel: "steelMill",
  factory: "electronicsFactory",
  munitionsPlant: "munitionsFactory",
  uraniumExtractor: "uraniumEnrichment",
  rareEarthMine: "rareEarthExtractor",
  compositesPlant: "compositesLab",
  powerPlant: "powerPlant",
  barracks: "barracks",
  missileSilo: "missileSilo",
  antiMissileBattery: "antiMissileBattery",
  radarStation: "radarStation",
  bunker: "bunker",
  spyAcademy: "spyAcademy",
  capital: "capital",
  marketplace: "marketplace",
  tradePort: "marketplace",
  researchLab: "compositesLab",
};
const buildingSpriteTextures: Record<string, Texture> = {};
let spritesLoaded = false;

async function loadBuildingSprites() {
  for (const type of BUILDING_SPRITE_TYPES) {
    try {
      const tex = await Assets.load(`/sprites/${type}.webp`);
      buildingSpriteTextures[type] = tex;
    } catch (e) {
      console.warn(`Failed to load sprite for ${type}:`, e);
    }
  }
  // Also load resource sprites
  for (const rType of ["steel", "fuel", "composites", "rareEarth", "uranium"]) {
    try {
      const tex = await Assets.load(`/sprites/resources/${rType}.webp`);
      buildingSpriteTextures[rType] = tex;
    } catch (e) {
      console.warn(`Failed to load resource sprite for ${rType}:`, e);
    }
  }
  spritesLoaded = true;
  console.log(`Loaded ${Object.keys(buildingSpriteTextures).length} total sprites`);
  // Signal that sprites are ready - next GAME_STATE will use them
  window.dispatchEvent(new CustomEvent("ironpact-sprites-loaded"));
}

function createBuildingVisual(type: string, x: number, y: number, isBuilding: boolean): Container {
  const spriteKey = BUILDING_SPRITE_MAP[type] || type;
  const tex = buildingSpriteTextures[spriteKey];
  if (tex && spritesLoaded) {
    const sprite = new Sprite(tex);
    sprite.anchor.set(0.5);
    sprite.x = x;
    sprite.y = y - 2;
    sprite.width = 30;
    sprite.height = 30;
    if (isBuilding) sprite.alpha = 0.45;
    else sprite.alpha = 0.95;
    return sprite;
  }
  // Fallback: orange circle
  const gfx = new Graphics();
  const bColor = isBuilding ? 0x555555 : 0xff6a00;
  const bAlpha = isBuilding ? 0.5 : 0.9;
  gfx.circle(x, y - 2, 5 * 0.5);
  gfx.fill({ color: bColor, alpha: bAlpha });
  if (isBuilding) gfx.alpha = 0.45;
  return gfx;
}

function hexToPixel(q: number, r: number) {
  return {
    x: HEX_SIZE * (3 / 2 * q),
    y: HEX_SIZE * (Math.sqrt(3) / 2 * q + Math.sqrt(3) * r),
  };
}

function pixelToHex(px: number, py: number) {
  const q = (2 / 3 * px) / HEX_SIZE;
  const r = (-1 / 3 * px + Math.sqrt(3) / 3 * py) / HEX_SIZE;
  const s = -q - r;
  let rq = Math.round(q), rr = Math.round(r), rs = Math.round(s);
  const dq = Math.abs(rq - q), dr = Math.abs(rr - r), ds = Math.abs(rs - s);
  if (dq > dr && dq > ds) rq = -rr - rs;
  else if (dr > ds) rr = -rq - rs;
  return { q: rq, r: rr };
}

function getHexCorners(cx: number, cy: number, size: number): number[] {
  const corners: number[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 180) * (60 * i);
    corners.push(cx + size * Math.cos(angle));
    corners.push(cy + size * Math.sin(angle));
  }
  return corners;
}

const NEIGHBOR_DIRS: [number, number][] = [
  [+1,  0], [ 0, +1], [-1, +1], [-1,  0], [ 0, -1], [+1, -1],
];

function drawTerritoryBorders(
  gfx: Graphics, tile: HexTile, tileMap: Map<string, HexTile>, pColor: number, size: number,
) {
  const { x, y } = hexToPixel(tile.q, tile.r);
  for (let edge = 0; edge < 6; edge++) {
    const [dq, dr] = NEIGHBOR_DIRS[edge];
    const adjKey = `${tile.q + dq},${tile.r + dr}`;
    const adj = tileMap.get(adjKey);
    if (!adj || adj.ownerId !== tile.ownerId) {
      const angle1 = (Math.PI / 180) * (60 * edge);
      const angle2 = (Math.PI / 180) * (60 * ((edge + 1) % 6));
      gfx.moveTo(x + size * Math.cos(angle1), y + size * Math.sin(angle1));
      gfx.lineTo(x + size * Math.cos(angle2), y + size * Math.sin(angle2));
      gfx.stroke({ width: 3.5, color: pColor, alpha: 0.95 });
    }
  }
}

// Biome texture overlays
function drawBiomeTexture(gfx: Graphics, biome: string, x: number, y: number, hash: number) {
  const rng = (seed: number) => ((seed * 16807 + 0) % 2147483647) / 2147483647;
  if (biome === "forest") {
    // Tiny triangle trees
    for (let i = 0; i < 3; i++) {
      const ox = (rng(hash + i * 7) - 0.5) * 16;
      const oy = (rng(hash + i * 13) - 0.5) * 14;
      const sz = 2 + rng(hash + i * 19) * 2;
      gfx.moveTo(x + ox, y + oy - sz); gfx.lineTo(x + ox + sz * 0.6, y + oy + sz * 0.4); gfx.lineTo(x + ox - sz * 0.6, y + oy + sz * 0.4);
      gfx.closePath(); gfx.fill({ color: 0x2a4a20, alpha: 0.4 + rng(hash + i * 31) * 0.2 });
    }
  } else if (biome === "mountain") {
    // Subtle rocky texture — small angular lines
    for (let i = 0; i < 3; i++) {
      const ox = (rng(hash + i * 11) - 0.5) * 14;
      const oy = (rng(hash + i * 17) - 0.5) * 12;
      const len = 2 + rng(hash + i * 23) * 3;
      const angle = rng(hash + i * 29) * Math.PI;
      gfx.moveTo(x + ox - Math.cos(angle) * len, y + oy - Math.sin(angle) * len);
      gfx.lineTo(x + ox + Math.cos(angle) * len, y + oy + Math.sin(angle) * len);
      gfx.stroke({ width: 0.5, color: 0x555566, alpha: 0.25 });
    }
  } else if (biome === "desert") {
    // Subtle sand dots
    for (let i = 0; i < 4; i++) {
      const ox = (rng(hash + i * 9) - 0.5) * 16;
      const oy = (rng(hash + i * 14) - 0.5) * 14;
      gfx.circle(x + ox, y + oy, 0.5 + rng(hash + i * 21) * 0.5);
      gfx.fill({ color: 0x5a4a30, alpha: 0.3 });
    }
  } else if (biome === "swamp") {
    // Wavy lines
    const ox = (rng(hash) - 0.5) * 10;
    const oy = (rng(hash + 5) - 0.5) * 8;
    gfx.moveTo(x + ox - 4, y + oy); gfx.lineTo(x + ox - 1, y + oy - 1.5); gfx.lineTo(x + ox + 2, y + oy + 1); gfx.lineTo(x + ox + 4, y + oy - 0.5);
    gfx.stroke({ width: 0.5, color: 0x3a5a3a, alpha: 0.35 });
  } else if (biome === "volcanic") {
    // Lava cracks
    const ox = (rng(hash) - 0.5) * 12;
    const oy = (rng(hash + 3) - 0.5) * 10;
    gfx.moveTo(x + ox - 3, y + oy); gfx.lineTo(x + ox, y + oy + 2); gfx.lineTo(x + ox + 3, y + oy - 1);
    gfx.stroke({ width: 0.6, color: 0xff3300, alpha: 0.2 });
  }
}

interface MissileAnim {
  id: string;
  from: { q: number; r: number };
  to: { q: number; r: number };
  launchedAt: number;
  eta: number;
  type: string;
  isCounter?: boolean;
  willIntercept?: boolean;
  interceptorHex?: { q: number; r: number };
}

interface HexMapProps {
  clearSelection?: number; // increment to trigger clear
  tiles: HexTile[];
  onHexClick?: (q: number, r: number, tile: HexTile | undefined) => void;
  playerColors?: Record<string, string>;
  centerOn?: { q: number; r: number };
  activeMissiles?: MissileAnim[];
  trainingHexes?: Record<string, { count: number; completesAt: number }>;
  tick?: number;
  commanderHexes?: Array<{ q: number; r: number; specialty: string; name: string }>;
}

function cssColorToNum(css: string): number {
  return parseInt(css.replace("#", ""), 16);
}

export function HexMap({ tiles, onHexClick, playerColors, centerOn, activeMissiles, clearSelection, commanderHexes }: HexMapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const appRef = useRef<Application | null>(null);
  const onHexClickRef = useRef(onHexClick);
  const tilesRef = useRef(tiles);
  const tileMapRef = useRef<Map<string, HexTile>>(new Map());
  const playerColorsRef = useRef(playerColors);
  const territoryGfxRef = useRef<Graphics | null>(null);
  const buildingContainerRef = useRef<Container | null>(null);
  const resourceSpriteContainerRef = useRef<Container | null>(null);
  const mapContainerRef = useRef<Container | null>(null);
  const fogGfxRef = useRef<Graphics | null>(null);
  const terrainGfxRef = useRef<Graphics | null>(null);
  const textureGfxRef = useRef<Graphics | null>(null);
  const resourceGfxRef = useRef<Graphics | null>(null);
  const resourceSpriteContainerRef2 = useRef<Container | null>(null);
  const activeMissilesRef = useRef<MissileAnim[]>([]);

  const commanderHexesRef = useRef(commanderHexes || []);
  const centerOnRef = useRef(centerOn);
  const hasCenteredRef = useRef(false);

  onHexClickRef.current = onHexClick;
  tilesRef.current = tiles;
  commanderHexesRef.current = commanderHexes || [];
  const cmdKey = useMemo(() => JSON.stringify((commanderHexes || []).map(c => `${c.q},${c.r}`).sort()), [commanderHexes]);
  playerColorsRef.current = playerColors;
  centerOnRef.current = centerOn;
  activeMissilesRef.current = activeMissiles || [];

  // === CENTER ON CAPITAL when data arrives ===
  useEffect(() => {
    if (centerOn && !hasCenteredRef.current && appRef.current && mapContainerRef.current) {
      const { x: cx, y: cy } = hexToPixel(centerOn.q, centerOn.r);
      const mc = mapContainerRef.current;
      mc.scale.set(1.5);
      mc.pivot.set(cx, cy);
      mc.x = appRef.current.screen.width / 2;
      mc.y = appRef.current.screen.height / 2;
      hasCenteredRef.current = true;
    }
  }, [centerOn, tiles]);

  // === REACTIVE UPDATES ===
  // Spread heavy redraws across frames to avoid jitter
  const redrawIdRef = useRef(0);
  useEffect(() => {
    const tileMap = new Map<string, HexTile>();
    for (const t of tiles) tileMap.set(`${t.q},${t.r}`, t);
    tileMapRef.current = tileMap;

    // Cancel any pending staged redraws
    const id = ++redrawIdRef.current;

    // Frame 1: Territory (most important — shows ownership changes)
    requestAnimationFrame(() => {
      if (redrawIdRef.current !== id) return;
      if (territoryGfxRef.current) {
        const gfx = territoryGfxRef.current;
        gfx.clear();
        const colors = playerColorsRef.current || {};
        for (const tile of tiles) {
          if (!tile.ownerId || !colors[tile.ownerId]) continue;
          const { x, y } = hexToPixel(tile.q, tile.r);
          const pColor = cssColorToNum(colors[tile.ownerId]);
          const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
          gfx.poly(corners);
          gfx.fill({ color: pColor, alpha: 0.25 });
          const innerCorners = getHexCorners(x, y, HEX_SIZE - 1);
          gfx.poly(innerCorners);
          gfx.stroke({ width: 1, color: pColor, alpha: 0.28 });
          drawTerritoryBorders(gfx, tile, tileMap, pColor, HEX_SIZE + 0.5);
        }
        // Clear optimistic claim overlays now that real territory is drawn
        const anims = (window as any).__ironpactAnims;
        if (anims) anims.claims = [];
      }

      // Frame 2: Fog
      requestAnimationFrame(() => {
        if (redrawIdRef.current !== id) return;
        if (fogGfxRef.current) {
          const fg = fogGfxRef.current;
          fg.clear();
          for (const tile of tiles) {
            if ((tile as any).visible) continue;
            const { x, y } = hexToPixel(tile.q, tile.r);
            const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
            fg.poly(corners);
            fg.fill({ color: 0x050810, alpha: tile.ownerId ? 0.7 : 0.6 });
          }
        }

        // Frame 3: Terrain + Textures (only on initial load, skip on deltas)
        if (tiles.length > 0 && !(window as any).__ironpactTerrainDrawn) {
          requestAnimationFrame(() => {
            if (redrawIdRef.current !== id) return;
            if (terrainGfxRef.current) {
              const tg = terrainGfxRef.current;
              tg.clear();
              for (const tile of tiles) {
                const { x, y } = hexToPixel(tile.q, tile.r);
                const color = hexColor(tile.q, tile.r, tile.biome, tile.elevation, tile.moisture);
                const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
                tg.poly(corners);
                tg.fill(color);
                tg.poly(corners);
                if (tile.biome === "ocean") {
                  tg.stroke({ width: 0.4, color: 0x0a1a30, alpha: 0.3 });
                } else {
                  tg.stroke({ width: 0.4, color: 0xffffff, alpha: 0.04 });
                }
              }
            }
            if (textureGfxRef.current) {
              const txg = textureGfxRef.current;
              txg.clear();
              for (const tile of tiles) {
                if (tile.biome === "ocean" || tile.biome === "coast") continue;
                const { x, y } = hexToPixel(tile.q, tile.r);
                const hash = ((tile.q * 374761393 + tile.r * 668265263) ^ (tile.q * 1274126177)) >>> 0;
                drawBiomeTexture(txg, tile.biome, x, y, hash);
              }
            }
            (window as any).__ironpactTerrainDrawn = true;
          });
        }
      });
    });

    // Redraw resource indicators
    if (resourceGfxRef.current) {
      const rg = resourceGfxRef.current;
      rg.clear();
      // Resource sprite container
      if (!resourceSpriteContainerRef.current) {
        const rsc = new Container();
        rsc.zIndex = 3;
        // Find mapContainer and add
        const mc = rg.parent;
        if (mc) { mc.addChild(rsc); resourceSpriteContainerRef.current = rsc; }
      }
      if (resourceSpriteContainerRef.current) {
        resourceSpriteContainerRef.current.removeChildren();
      }
      for (const tile of tiles) {
        if (!tile.resource || !(tile as any).visible) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        const info = RESOURCE_INDICATORS[tile.resource.type];
        if (!info) continue;
        // Uranium glow ring
        if (tile.resource.type === "uranium") {
          rg.circle(x, y, 7);
          rg.fill({ color: 0x00ff88, alpha: 0.08 });
          rg.circle(x, y, 5);
          rg.stroke({ width: 0.5, color: 0x00ff88, alpha: 0.25 });
        }
        // Resource sprite or fallback diamond
        const spriteKey = RESOURCE_SPRITE_MAP[tile.resource.type];
        const tex = spriteKey ? buildingSpriteTextures[spriteKey] : null;
        if (tex && resourceSpriteContainerRef.current) {
          const rs = new Sprite(tex);
          rs.anchor.set(0.5);
          rs.x = x;
          rs.y = y;
          rs.width = 14;
          rs.height = 14;
          rs.alpha = 0.85;
          resourceSpriteContainerRef.current.addChild(rs);
        } else {
          // Fallback diamond
          const s = tile.resource.type === "uranium" ? 5 : 3;
          rg.poly([x, y - s, x + s, y, x, y + s, x - s, y]);
          rg.fill(info.color);
          rg.stroke({ width: 0.5, color: 0x000000, alpha: 0.3 });
        }
      }
    }

    if (buildingContainerRef.current) {
      const bc = buildingContainerRef.current;
      bc.removeChildren();
      const now = Date.now();
      for (const tile of tiles) {
        const hasCmdHere = commanderHexesRef.current?.some((ch: any) => ch.q === tile.q && ch.r === tile.r);
        if ((!tile.buildings || tile.buildings.length === 0) && !(tile.troops && tile.troops > 0) && !hasCmdHere) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        for (const b of (tile.buildings || [])) {
          const cfg = BUILDING_CONFIGS[b.type as keyof typeof BUILDING_CONFIGS] || (b.type === "capital" ? { icon: "🏛️", name: "Capital" } : null);
          if (!cfg) continue;
          const isBuilding = b.completedAt ? b.completedAt > now : false;
          // Building sprite
          const bVisual = createBuildingVisual(b.type, x, y, isBuilding);
          bc.addChild(bVisual);
          // Level indicator: small dots below building (Lv2=1 dot, Lv3=2 dots)
          const bLevel = b.level || 1;
          if (bLevel > 1 && !isBuilding) {
            const lvGfx = new Graphics();
            const dotCount = bLevel - 1;
            const dotSpacing = 4;
            const startX = x - (dotCount - 1) * dotSpacing / 2;
            for (let d = 0; d < dotCount; d++) {
              lvGfx.circle(startX + d * dotSpacing, y + 6, 1.5);
            }
            lvGfx.fill({ color: 0xffd700, alpha: 0.9 });
            bc.addChild(lvGfx);
          }
        }
        if (tile.troops && tile.troops > 0) {
          // Troop badge with background
          const badgeBg = new Graphics();
          badgeBg.roundRect(x - 10, y + 5, 20, 10, 3);
          badgeBg.fill({ color: 0x000000, alpha: 0.6 });
          bc.addChild(badgeBg);

          // Shield icon
          const shieldGfx = new Graphics();
          shieldGfx.moveTo(x, y + 6); shieldGfx.lineTo(x + 6, y + 8); shieldGfx.lineTo(x + 5, y + 13); shieldGfx.lineTo(x, y + 16); shieldGfx.lineTo(x - 5, y + 13); shieldGfx.lineTo(x - 6, y + 8);
          shieldGfx.closePath(); shieldGfx.fill({ color: 0xff6a00, alpha: 0.25 }); shieldGfx.stroke({ width: 0.5, color: 0xff6a00, alpha: 0.6 });
          bc.addChild(shieldGfx);
          const troopLabel = new Text({
            text: `${tile.troops}`,
            style: new TextStyle({
              fontSize: 7,
              fill: "#ffcc00",
              fontWeight: "bold",
              dropShadow: { color: "#000000", blur: 1, distance: 0, alpha: 0.9 },
            }),
          });
          troopLabel.anchor.set(0.5);
          troopLabel.x = x;
          troopLabel.y = y + 11;
          bc.addChild(troopLabel);
        }

        // Commander star marker (reactive) — uses commanderHexes prop for live updates
        const isCmd = commanderHexesRef.current?.some((ch: any) => ch.q === tile.q && ch.r === tile.r);
        if (isCmd) {
          const cmdStar = new Text({
            text: "\u2B50",
            style: new TextStyle({ fontSize: 14, fill: "#ffd700" }),
          });
          cmdStar.anchor.set(0.5);
          cmdStar.x = x;
          cmdStar.y = y + (tile.buildings?.length ? -18 : -4);
          bc.addChild(cmdStar);
        }
      }
    }
  }, [tiles, cmdKey]);

  // === PIXI INIT ===
  useEffect(() => {
    if (!containerRef.current || appRef.current) return;
    let destroyed = false;

    (async () => {
      const app = new Application();
      await loadBuildingSprites(); (window as any).__ironpactSpritesReady = true;
      
      // Re-render buildings when sprites finish loading
      const onSpritesLoaded = () => {
        if (!tilesRef.current || !buildingContainerRef.current) return;
        const bCon = buildingContainerRef.current;
        bCon.removeChildren();
        const now = Date.now();
        for (const tile of tilesRef.current) {
          if (!tile.buildings || tile.buildings.length === 0) continue;
          const { x, y } = hexToPixel(tile.q, tile.r);
          for (const b of (tile.buildings || [])) {
            const isBuilding = b.completedAt ? b.completedAt > now : false;
            const bVisual = createBuildingVisual(b.type, x, y, isBuilding);
            bCon.addChild(bVisual);
            // Level dots
            const bLevel = b.level || 1;
            if (bLevel > 1 && !isBuilding) {
              const lvGfx = new Graphics();
              const dotCount = bLevel - 1;
              const dotSpacing = 4;
              const startX = x - (dotCount - 1) * dotSpacing / 2;
              for (let d = 0; d < dotCount; d++) {
                lvGfx.circle(startX + d * dotSpacing, y + 6, 1.5);
              }
              lvGfx.fill({ color: 0xffd700, alpha: 0.9 });
              bCon.addChild(lvGfx);
            }
          }
          // Troop badges
          if (tile.troops && tile.troops > 0) {
            const badgeBg = new Graphics();
            badgeBg.roundRect(x - 10, y + 5, 20, 10, 3);
            badgeBg.fill({ color: 0x000000, alpha: 0.6 });
            bCon.addChild(badgeBg);
            const troopLabel = new Text({
              text: `${tile.troops}`,
              style: new TextStyle({ fontSize: 7, fill: "#ffcc00", fontWeight: "bold", dropShadow: { color: "#000000", blur: 1, distance: 0, alpha: 0.9 } }),
            });
            troopLabel.anchor.set(0.5);
            troopLabel.x = x;
            troopLabel.y = y + 10;
            bCon.addChild(troopLabel);
          }
        }
        console.log("Buildings redrawn with sprites");
      };
      window.addEventListener("ironpact-sprites-loaded", onSpritesLoaded);
      await app.init({
        resizeTo: containerRef.current!,
        background: 0x060b06,
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
      });
      if (destroyed) { app.destroy(true); return; }
      containerRef.current!.appendChild(app.canvas);
      appRef.current = app;

      const mapContainer = new Container();
      mapContainerRef.current = mapContainer;
      app.stage.addChild(mapContainer);

      // === TERRAIN ===
      const terrainGfx = new Graphics();
      mapContainer.addChild(terrainGfx);
      terrainGfxRef.current = terrainGfx;

      for (const tile of tilesRef.current) {
        const { x, y } = hexToPixel(tile.q, tile.r);
        const color = hexColor(tile.q, tile.r, tile.biome, tile.elevation, tile.moisture);
        const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
        terrainGfx.poly(corners);
        terrainGfx.fill(color);
        terrainGfx.poly(corners);
        if (tile.biome === "ocean") {
          terrainGfx.stroke({ width: 0.4, color: 0x0a1a30, alpha: 0.3 });
        } else {
          terrainGfx.stroke({ width: 0.5, color: 0x000000, alpha: 0.15 });
        }
      }

      // === BIOME TEXTURES ===
      const textureGfx = new Graphics();
      textureGfx.zIndex = 1;
      mapContainer.addChild(textureGfx);
      textureGfxRef.current = textureGfx;

      for (const tile of tilesRef.current) {
        if (tile.biome === "ocean" || tile.biome === "coast") continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        const hash = ((tile.q * 374761393 + tile.r * 668265263) ^ (tile.q * 1274126177)) >>> 0;
        drawBiomeTexture(textureGfx, tile.biome, x, y, hash);
      }

      // === OCEAN ANIMATION ===
      const oceanGfx = new Graphics();
      oceanGfx.zIndex = 1;
      mapContainer.addChild(oceanGfx);
      const oceanTiles = tilesRef.current.filter(t => t.biome === "ocean" || t.biome === "coast");
      let wavePhase = 0;

      // Animation overlay layer
      const animGfx = new Graphics();
      animGfx.zIndex = 6;
      mapContainer.addChild(animGfx);

      app.ticker.add(() => {
        wavePhase += 0.008;
        oceanGfx.clear();

        // === CLAIM PULSE ANIMATIONS ===
        animGfx.clear();
        const now = Date.now();
        const anims = (window as any).__ironpactAnims || { pulses: [], moves: [] };
        const pulses = anims.pulses;
        for (let i = pulses.length - 1; i >= 0; i--) {
          const p = pulses[i];
          const age = (now - p.time) / 600; // 600ms animation
          if (age > 1) { pulses.splice(i, 1); continue; }
          const { x, y } = hexToPixel(p.q, p.r);
          const radius = HEX_SIZE * (0.5 + age * 1.5);
          const alpha = 0.6 * (1 - age);
          animGfx.circle(x, y, radius);
          animGfx.stroke({ width: 2, color: p.color || 0x00ffaa, alpha });
          // Inner flash
          if (age < 0.3) {
            const corners = getHexCorners(x, y, HEX_SIZE);
            animGfx.poly(corners);
            animGfx.fill({ color: p.color || 0x00ffaa, alpha: 0.3 * (1 - age / 0.3) });
          }
        }

        // === OPTIMISTIC CLAIM OVERLAY (persists until STATE_DELTA) ===
        const claims = anims.claims || [];
        for (let i = claims.length - 1; i >= 0; i--) {
          const c = claims[i];
          // Keep for 3s max — STATE_DELTA should arrive within ~200ms
          if (now - c.time > 3000) { claims.splice(i, 1); continue; }
          const { x, y } = hexToPixel(c.q, c.r);
          const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
          animGfx.poly(corners);
          animGfx.fill({ color: c.color, alpha: 0.25 });
        }

        // === RESOURCE NODE PULSE ===
        const resTiles = tilesRef.current;
        if (resTiles) {
          const pulse = Math.sin(now * 0.003) * 0.5 + 0.5;
          for (const tile of resTiles) {
            if (!tile.resource || !(tile as any).visible) continue;
            const info = ({"steel": 0x8899bb, "fuel": 0xff8800, "composites": 0xaa8833, "rareEarth": 0xaa55ee, "uranium": 0x00ff88} as any)[tile.resource.type];
            if (!info) continue;
            const { x, y } = hexToPixel(tile.q, tile.r);
            const r = 4 + pulse * 2;
            animGfx.circle(x, y, r);
            animGfx.fill({ color: info, alpha: 0.08 + pulse * 0.06 });
          }
        }

        // === BUILDING PROGRESS INDICATORS ===
        const buildingHexes = (window as any).__ironpactBuildingHexes;
        if (buildingHexes) {
          for (const bh of buildingHexes) {
            const elapsed = now - bh.startedAt;
            const duration = bh.completesAt - bh.startedAt;
            if (duration <= 0 || elapsed >= duration) continue;
            const progress = Math.min(elapsed / duration, 1);
            const { x, y } = hexToPixel(bh.q, bh.r);
            // Background arc (dark)
            const radius = HEX_SIZE + 3;
            animGfx.arc(x, y, radius, -Math.PI / 2, Math.PI * 2 - Math.PI / 2);
            animGfx.stroke({ width: 2, color: 0x333333, alpha: 0.4 });
            // Progress arc (orange)
            const endAngle = -Math.PI / 2 + progress * Math.PI * 2;
            animGfx.arc(x, y, radius, -Math.PI / 2, endAngle);
            animGfx.stroke({ width: 2, color: 0xff8800, alpha: 0.8 });
            // Percent text would need PIXI.Text — skip for perf, the arc is enough
          }
        }

        // === TROOP MOVE LINE FLASH ===
        const moves = anims.moves;
        for (let i = moves.length - 1; i >= 0; i--) {
          const m = moves[i];
          const age = (now - m.time) / 400; // 400ms flash
          if (age > 1) { moves.splice(i, 1); continue; }
          const from = hexToPixel(m.fromQ, m.fromR);
          const to = hexToPixel(m.toQ, m.toR);
          const alpha = 0.8 * (1 - age);
          // Animated head position
          const headT = Math.min(age * 2, 1);
          const hx = from.x + (to.x - from.x) * headT;
          const hy = from.y + (to.y - from.y) * headT;
          animGfx.moveTo(from.x, from.y);
          animGfx.lineTo(hx, hy);
          animGfx.stroke({ width: 1.5, color: 0xffcc00, alpha });
          // Head dot
          animGfx.circle(hx, hy, 2);
          animGfx.fill({ color: 0xffcc00, alpha });
        }

        for (const tile of oceanTiles) {
          const { x, y } = hexToPixel(tile.q, tile.r);
          const wave = Math.sin(wavePhase + tile.q * 0.5 + tile.r * 0.3) * 0.5 + 0.5;
          const wave2 = Math.sin(wavePhase * 0.7 + tile.q * 0.3 - tile.r * 0.4) * 0.5 + 0.5;
          const ox = wave * 4 - 2;
          oceanGfx.moveTo(x - 5 + ox, y - 1);
          oceanGfx.lineTo(x + 5 + ox, y - 1);
          oceanGfx.stroke({ width: 0.5, color: 0x0e2245, alpha: 0.08 + wave2 * 0.05 });
          const ox2 = wave2 * 3 - 1.5;
          oceanGfx.moveTo(x - 3 + ox2, y + 2);
          oceanGfx.lineTo(x + 3 + ox2, y + 2);
          oceanGfx.stroke({ width: 0.3, color: 0x0e2240, alpha: 0.04 + wave * 0.03 });
        }
      });

      // === TERRITORY ===
      const territoryGfx = new Graphics();
      territoryGfx.zIndex = 2;
      mapContainer.addChild(territoryGfx);
      territoryGfxRef.current = territoryGfx;

      const colors = playerColorsRef.current || {};
      for (const tile of tilesRef.current) {
        if (!tile.ownerId || !colors[tile.ownerId]) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        const pColor = cssColorToNum(colors[tile.ownerId]);
        const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
        territoryGfx.poly(corners);
        territoryGfx.fill({ color: pColor, alpha: 0.28 });
        const innerCorners = getHexCorners(x, y, HEX_SIZE - 1);
        territoryGfx.poly(innerCorners);
        territoryGfx.stroke({ width: 1, color: pColor, alpha: 0.3 });
        drawTerritoryBorders(territoryGfx, tile, tileMapRef.current, pColor, HEX_SIZE + 0.5);
      }

      // === RESOURCES ===
      const resourceGfx = new Graphics();
      resourceGfx.zIndex = 5;
      mapContainer.addChild(resourceGfx);
      resourceGfxRef.current = resourceGfx;

      // Resource sprite container for initial draw
      const initResourceContainer = new Container();
      initResourceContainer.zIndex = 5;
      mapContainer.addChild(initResourceContainer);
      resourceSpriteContainerRef.current = initResourceContainer;

      for (const tile of tilesRef.current) {
        if (!tile.resource || !(tile as any).visible) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        const info = RESOURCE_INDICATORS[tile.resource.type];
        if (!info) continue;
        // Uranium glow
        if (tile.resource.type === "uranium") {
          resourceGfx.circle(x, y - 6, 8);
          resourceGfx.fill({ color: 0x2a9d8f, alpha: 0.1 });
          resourceGfx.circle(x, y - 6, 6);
          resourceGfx.stroke({ width: 1, color: 0x2a9d8f, alpha: 0.3 });
        }
        // Resource sprite or fallback pill
        const spriteKey = RESOURCE_SPRITE_MAP[tile.resource.type];
        const tex = spriteKey ? buildingSpriteTextures[spriteKey] : null;
        if (tex) {
          const rs = new Sprite(tex);
          rs.anchor.set(0.5);
          rs.x = x;
          rs.y = y;
          rs.width = 14;
          rs.height = 14;
          rs.alpha = 0.85;
          initResourceContainer.addChild(rs);
        } else {
          // Fallback: pill + label (sprites may not be loaded yet on first draw)
          resourceGfx.roundRect(x - 8, y - 12, 16, 10, 3);
          resourceGfx.fill({ color: info.bg, alpha: 0.85 });
          resourceGfx.stroke({ width: 0.5, color: 0x000000, alpha: 0.4 });
          const rLabel = new Text({ text: info.label, style: { fontFamily: "Rajdhani", fontSize: 7, fontWeight: "700", fill: 0xffffff } });
          rLabel.anchor.set(0.5, 0.5);
          rLabel.x = x;
          rLabel.y = y - 7;
          initResourceContainer.addChild(rLabel);
        }
      }

      // === BUILDINGS (emoji with drop shadow) ===
      const buildingContainer = new Container();
      buildingContainer.zIndex = 7;
      mapContainer.addChild(buildingContainer);
      buildingContainerRef.current = buildingContainer;
      (buildingContainer as any).__buildingLayer = true;

      const now = Date.now();
      for (const tile of tilesRef.current) {
        if (!tile.buildings || tile.buildings.length === 0) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        for (const b of (tile.buildings || [])) {
          const cfg = BUILDING_CONFIGS[b.type as keyof typeof BUILDING_CONFIGS] || (b.type === "capital" ? { icon: "🏛️", name: "Capital" } : null);
          if (!cfg) continue;
          const isBuilding = b.completedAt ? b.completedAt > now : false;
          // Building sprite
          const bVisual2 = createBuildingVisual(b.type, x, y, isBuilding);
          buildingContainer.addChild(bVisual2);
        }
        if (tile.troops && tile.troops > 0) {
          const badgeBg = new Graphics();
          badgeBg.roundRect(x - 10, y + 5, 20, 10, 3);
          badgeBg.fill({ color: 0x000000, alpha: 0.6 });
          buildingContainer.addChild(badgeBg);

          // Shield icon
          const shieldGfx = new Graphics();
          shieldGfx.moveTo(x, y + 6); shieldGfx.lineTo(x + 6, y + 8); shieldGfx.lineTo(x + 5, y + 13); shieldGfx.lineTo(x, y + 16); shieldGfx.lineTo(x - 5, y + 13); shieldGfx.lineTo(x - 6, y + 8);
          shieldGfx.closePath(); shieldGfx.fill({ color: 0xff6a00, alpha: 0.25 }); shieldGfx.stroke({ width: 0.5, color: 0xff6a00, alpha: 0.6 });
          bc.addChild(shieldGfx);
          const troopLabel = new Text({
            text: `${tile.troops}`,
            style: new TextStyle({
              fontSize: 7,
              fill: "#ffcc00",
              fontWeight: "bold",
              dropShadow: { color: "#000000", blur: 1, distance: 0, alpha: 0.9 },
            }),
          });
          troopLabel.anchor.set(0.5);
          troopLabel.x = x;
          troopLabel.y = y + 11;
          buildingContainer.addChild(troopLabel);
        }


      }

      // === IRRADIATED HEXES ===
      const irradGfx = new Graphics();
      irradGfx.zIndex = 7;
      mapContainer.addChild(irradGfx);
      app.ticker.add(() => {
        irradGfx.clear();
        const nowIr = Date.now();
        for (const [, tile] of tileMapRef.current) {
          if ((tile as any).irradiatedUntil && (tile as any).irradiatedUntil > nowIr) {
            const { x, y } = hexToPixel(tile.q, tile.r);
            const corners = getHexCorners(x, y, HEX_SIZE - 1);
            const pulse = 0.3 + 0.15 * Math.sin(nowIr / 500 + tile.q * 0.5);
            irradGfx.poly(corners);
            irradGfx.fill({ color: 0x00ff44, alpha: pulse * 0.12 });
            irradGfx.circle(x, y, 3);
            irradGfx.fill({ color: 0x00ff44, alpha: pulse * 0.5 });
          }
        }
      });

      // === FOG ===
      const fogGfx = new Graphics();
      fogGfx.zIndex = 8;
      mapContainer.addChild(fogGfx);
      fogGfxRef.current = fogGfx;

      for (const tile of tilesRef.current) {
        if ((tile as any).visible) continue;
        const { x, y } = hexToPixel(tile.q, tile.r);
        const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
        fogGfx.poly(corners);
        fogGfx.fill({ color: 0x000000, alpha: tile.ownerId ? 0.65 : 0.55 });
      }

      // === MISSILES ===
      const missileGfx = new Graphics();
      missileGfx.zIndex = 9;
      mapContainer.addChild(missileGfx);

      // Smoke particle pool
      const smokeParticles: Array<{ x: number; y: number; vx: number; vy: number; life: number; maxLife: number; size: number; color: number }> = [];
      // Impact explosion pool
      const impacts: Array<{ x: number; y: number; time: number; nuclear: boolean; isIntercept: boolean }> = [];
      const impactedMissiles = new Set<string>();

      app.ticker.add(() => {
        missileGfx.clear();
        const now = Date.now();
        // Cleanup: if no active missiles, reset tracking
        if (activeMissilesRef.current.length === 0 && impactedMissiles.size > 0) {
          const anyActive = impacts.some(imp => (now - imp.time) < 3500);
          if (!anyActive) { impactedMissiles.clear(); impacts.length = 0; smokeParticles.length = 0; }
        }

        // === CAP: skip rendering if too many missiles ===
        const maxRender = 10;
        const missiles = activeMissilesRef.current.length > maxRender ? activeMissilesRef.current.slice(-maxRender) : activeMissilesRef.current;
        // === FIRST PASS: get pixel positions of non-counter missiles ===
        const mPos: Record<string, { x: number; y: number }> = {};
        for (const m of missiles) {
          if (m.isCounter || now < m.launchedAt || now >= m.eta) continue;
          const p = Math.max(0, Math.min(1, (now - m.launchedAt) / (m.eta - m.launchedAt)));
          const fp = hexToPixel(m.from.q, m.from.r);
          const tp = hexToPixel(m.to.q, m.to.r);
          const mx = fp.x + (tp.x - fp.x) * p;
          const my = fp.y + (tp.y - fp.y) * p;
          const d = Math.hypot(tp.x - fp.x, tp.y - fp.y);
          mPos[m.id] = { x: mx, y: my - d * 0.2 * 4 * p * (1 - p) };
        }

        // === ACTIVE MISSILES ===
        for (const m of missiles) {
          const mKey = m.from.q + "," + m.from.r + ">" + m.to.q + "," + m.to.r + "@" + m.launchedAt;
          if (now < m.launchedAt) continue;
          if (now >= m.eta) {
            if (!impactedMissiles.has(mKey)) {
              impactedMissiles.add(mKey);
              // Counter: explode at target missile's position
              const tmId = (m as any).targetMissileId;
              const impP = (tmId && mPos[tmId]) ? mPos[tmId] : hexToPixel(m.to.q, m.to.r);
              impacts.push({ x: impP.x, y: impP.y, time: now, nuclear: m.type === "nuclear", isIntercept: !!m.isCounter });
            }
            continue;
          }
          const progress = Math.max(0, Math.min(1, (now - m.launchedAt) / (m.eta - m.launchedAt)));
          const fromPx = hexToPixel(m.from.q, m.from.r);
          const tmId2 = (m as any).targetMissileId;
          const trackedPos = (tmId2 && mPos[tmId2]) ? mPos[tmId2] : null;
          const fallbackTo = hexToPixel(m.to.q, m.to.r);
          const toPx = trackedPos || fallbackTo;
          const dist = Math.hypot(fallbackTo.x - fromPx.x, fallbackTo.y - fromPx.y);
          const arcHeight = dist * 0.2;

          // Position: smooth pursuit for tracking counters
          let cx: number, arcY: number;
          if (trackedPos) {
            // Blend: early flight follows own trajectory, late flight follows target
            const ownX = fromPx.x + (fallbackTo.x - fromPx.x) * progress;
            const ownY = fromPx.y + (fallbackTo.y - fromPx.y) * progress;
            const ownArcY = ownY - arcHeight * 4 * progress * (1 - progress);
            // Blend factor: 0 at start, 1 at end (cubic ease-in for smooth homing)
            const blend = progress * progress * progress;
            cx = ownX + (trackedPos.x - ownX) * blend;
            arcY = ownArcY + (trackedPos.y - ownArcY) * blend;
          } else {
            cx = fromPx.x + (toPx.x - fromPx.x) * progress;
            const cy = fromPx.y + (toPx.y - fromPx.y) * progress;
            arcY = cy - arcHeight * 4 * progress * (1 - progress);
          }

          // Direction for warhead rotation (uses same blend as position)
          const nextT = Math.min(progress + 0.02, 1);
          let nx: number, ny: number;
          if (trackedPos) {
            const ownNx = fromPx.x + (fallbackTo.x - fromPx.x) * nextT;
            const ownNy = fromPx.y + (fallbackTo.y - fromPx.y) * nextT - arcHeight * 4 * nextT * (1 - nextT);
            const nb = nextT * nextT * nextT;
            nx = ownNx + (trackedPos.x - ownNx) * nb;
            ny = ownNy + (trackedPos.y - ownNy) * nb;
          } else {
            nx = fromPx.x + (toPx.x - fromPx.x) * nextT;
            ny = fromPx.y + (toPx.y - fromPx.y) * nextT - arcHeight * 4 * nextT * (1 - nextT);
          }
          const angle = Math.atan2(ny - arcY, nx - cx);

          const isNuke = m.type === "nuclear";
          const isCounter = m.isCounter;
          const headColor = isCounter ? 0x44aaff : isNuke ? 0x00ff88 : 0xff6a00;
          const trailColor = isCounter ? 0x2266aa : isNuke ? 0x007744 : 0xff3300;

          // === EXHAUST TRAIL (gradient fade) ===
          const trailLen = 0.2;
          for (let t = Math.max(0, progress - trailLen); t < progress; t += 0.015) {
            let tx: number, ty: number;
            if (trackedPos) {
              const ownTx = fromPx.x + (fallbackTo.x - fromPx.x) * t;
              const ownTy = fromPx.y + (fallbackTo.y - fromPx.y) * t - arcHeight * 4 * t * (1 - t);
              const b = t * t * t;
              tx = ownTx + (trackedPos.x - ownTx) * b;
              ty = ownTy + (trackedPos.y - ownTy) * b;
            } else {
              tx = fromPx.x + (toPx.x - fromPx.x) * t;
              ty = fromPx.y + (toPx.y - fromPx.y) * t - arcHeight * 4 * t * (1 - t);
            }
            const fade = (t - (progress - trailLen)) / trailLen;
            const width = 0.5 + fade * 2;
            missileGfx.circle(tx, ty, width);
            missileGfx.fill({ color: trailColor, alpha: fade * 0.5 });
          }

          // === SMOKE PARTICLES (spawn behind missile) ===
          if (smokeParticles.length < 80 && Math.random() < 0.15) {
            smokeParticles.push({
              x: cx + (Math.random() - 0.5) * 3,
              y: arcY + (Math.random() - 0.5) * 3,
              vx: (Math.random() - 0.5) * 0.3,
              vy: -Math.random() * 0.2 - 0.1,
              life: 0, maxLife: 30 + Math.random() * 20,
              size: 1 + Math.random() * 2,
              color: isNuke ? 0x225533 : 0x443322,
            });
          }

          // === WARHEAD — pointed diamond/arrow shape ===
          const len = isNuke ? 6 : 4.5;
          const wid = isNuke ? 3 : 2;
          // Arrow pointing in direction of travel
          const cos = Math.cos(angle);
          const sin = Math.sin(angle);
          // Nose
          const noseX = cx + cos * len;
          const noseY = arcY + sin * len;
          // Left fin
          const lx = cx - cos * len * 0.5 + sin * wid;
          const ly = arcY - sin * len * 0.5 - cos * wid;
          // Right fin
          const rx = cx - cos * len * 0.5 - sin * wid;
          const ry = arcY - sin * len * 0.5 + cos * wid;
          // Tail notch
          const tx2 = cx - cos * len * 0.3;
          const ty2 = arcY - sin * len * 0.3;

          missileGfx.moveTo(noseX, noseY);
          missileGfx.lineTo(lx, ly);
          missileGfx.lineTo(tx2, ty2);
          missileGfx.lineTo(rx, ry);
          missileGfx.closePath();
          missileGfx.fill({ color: headColor, alpha: 0.95 });

          // Glow halo
          const glowPulse = 0.7 + 0.3 * Math.sin(now * 0.01);
          missileGfx.circle(cx, arcY, (isNuke ? 10 : 7) * glowPulse);
          missileGfx.fill({ color: headColor, alpha: 0.12 });

          // === TARGET RETICLE ===
          if (!isCounter) {
            const pulse = 0.5 + 0.5 * Math.sin(now * 0.005);
            const r1 = 8 + pulse * 4;
            const r2 = 12 + pulse * 3;
            // Outer ring
            missileGfx.circle(toPx.x, toPx.y, r2);
            missileGfx.stroke({ width: 1, color: isNuke ? 0x00ff88 : 0xff0000, alpha: 0.3 + pulse * 0.2 });
            // Inner ring
            missileGfx.circle(toPx.x, toPx.y, r1);
            missileGfx.stroke({ width: 0.5, color: isNuke ? 0x00ff88 : 0xff0000, alpha: 0.5 });
            // Crosshairs
            missileGfx.moveTo(toPx.x - r2, toPx.y); missileGfx.lineTo(toPx.x - r1 + 2, toPx.y);
            missileGfx.moveTo(toPx.x + r1 - 2, toPx.y); missileGfx.lineTo(toPx.x + r2, toPx.y);
            missileGfx.moveTo(toPx.x, toPx.y - r2); missileGfx.lineTo(toPx.x, toPx.y - r1 + 2);
            missileGfx.moveTo(toPx.x, toPx.y + r1 - 2); missileGfx.lineTo(toPx.x, toPx.y + r2);
            missileGfx.stroke({ width: 0.5, color: isNuke ? 0x00ff88 : 0xff0000, alpha: 0.4 });
          }
        }

        // === SMOKE PARTICLES UPDATE ===
        for (let i = smokeParticles.length - 1; i >= 0; i--) {
          const p = smokeParticles[i];
          p.life++;
          if (p.life >= p.maxLife) { smokeParticles.splice(i, 1); continue; }
          p.x += p.vx;
          if (smokeParticles.length > 80) { smokeParticles.splice(0, smokeParticles.length - 40); }
          p.y += p.vy;
          const fade = 1 - p.life / p.maxLife;
          missileGfx.circle(p.x, p.y, p.size * fade);
          missileGfx.fill({ color: p.color, alpha: fade * 0.25 });
        }

        // === IMPACT EXPLOSIONS ===
        for (let i = impacts.length - 1; i >= 0; i--) {
          const imp = impacts[i];
          const age = (now - imp.time) / (imp.nuclear ? 1500 : 600);
          if (age > 1) { impacts.splice(i, 1); continue; }
          if (impacts.length > 8) { impacts.splice(0, impacts.length - 8); } // cap explosions
          const maxR = imp.nuclear ? 130 : 18;
          const r = age * maxR;
          const alpha = (1 - age) * 0.8;

          // Flash (first 20%)
          if (age < 0.2) {
            const flashR = age * 5 * maxR * 0.5;
            missileGfx.circle(imp.x, imp.y, flashR);
            const flashColor = imp.isIntercept ? 0x44aaff : imp.nuclear ? 0xffffff : 0xff6600;
            missileGfx.fill({ color: flashColor, alpha: (1 - age * 5) * 0.6 });
          }

          // Expanding shockwave ring
          const ringColor = imp.isIntercept ? 0x44aaff : imp.nuclear ? 0x00ff88 : 0xff4400;
          missileGfx.circle(imp.x, imp.y, r);
          missileGfx.stroke({ width: imp.nuclear ? 3 : 2, color: ringColor, alpha });

          // Intercept: second ring in red/green (enemy missile color)
          if (imp.isIntercept) {
            const r2 = age * maxR * 0.7;
            missileGfx.circle(imp.x, imp.y, r2);
            missileGfx.stroke({ width: 1.5, color: 0xff4400, alpha: alpha * 0.6 });
            // Debris sparks
            for (let s = 0; s < 4; s++) {
              const sparkAngle = (s / 4) * Math.PI * 2 + age * 3;
              const sparkR = r * 0.8;
              const sx = imp.x + Math.cos(sparkAngle) * sparkR;
              const sy = imp.y + Math.sin(sparkAngle) * sparkR;
              missileGfx.circle(sx, sy, 1.5 * (1 - age));
              missileGfx.fill({ color: 0xffaa00, alpha: alpha * 0.8 });
            }
          }

          if (imp.nuclear) {
            // Massive second shockwave
            const r2 = age * 0.7 * maxR;
            missileGfx.circle(imp.x, imp.y, r2);
            missileGfx.stroke({ width: 4, color: 0xffcc00, alpha: alpha * 0.8 });
            // Third shockwave (even bigger)
            const r3 = age * 0.5 * maxR * 1.3;
            missileGfx.circle(imp.x, imp.y, r3);
            missileGfx.stroke({ width: 2, color: 0xff4400, alpha: alpha * 0.4 });
            // Inner fireball (larger, longer)
            const fireR = maxR * 0.45 * (1 - age * 0.3);
            missileGfx.circle(imp.x, imp.y, fireR);
            missileGfx.fill({ color: 0xff6600, alpha: alpha * 0.6 });
            // White-hot core
            const coreR = maxR * 0.15 * (1 - age);
            missileGfx.circle(imp.x, imp.y, coreR);
            missileGfx.fill({ color: 0xffffff, alpha: alpha * 0.9 });
            // Radiation particles
            for (let s = 0; s < 8; s++) {
              const sparkAngle = (s / 8) * Math.PI * 2 + age * 4;
              const sparkDist = r2 * (0.6 + Math.random() * 0.4);
              const sx = imp.x + Math.cos(sparkAngle) * sparkDist;
              const sy = imp.y + Math.sin(sparkAngle) * sparkDist;
              missileGfx.circle(sx, sy, 2 * (1 - age));
              missileGfx.fill({ color: 0x00ff88, alpha: alpha * 0.7 });
            }
            // Screen flash for first 10%
            if (age < 0.1) {
              missileGfx.rect(-5000, -5000, 10000, 10000);
              missileGfx.fill({ color: 0xffffff, alpha: (1 - age * 10) * 0.3 });
            }
          }
        }

        // Clean old impact keys
        if (impactedMissiles.size > 50) impactedMissiles.clear();
      });

      // === SELECTION (pulsing) ===
      const selectionGfx = new Graphics();
      selectionGfx.zIndex = 10;
      mapContainer.addChild(selectionGfx);

      const rangeGfx = new Graphics();
      rangeGfx.zIndex = 4;
      mapContainer.addChild(rangeGfx);

      let selectedHex: { x: number; y: number } | null = null;
      (window as any).__ironpactClearSelection = () => { selectedHex = null; selectionGfx.clear(); };
      // Helper: get all hex positions within range
      function hexesInRange(cq: number, cr: number, range: number): Array<{q: number; r: number}> {
        const results: Array<{q: number; r: number}> = [];
        for (let dq = -range; dq <= range; dq++) {
          for (let dr = Math.max(-range, -dq - range); dr <= Math.min(range, -dq + range); dr++) {
            if (dq === 0 && dr === 0) continue;
            results.push({ q: cq + dq, r: cr + dr });
          }
        }
        return results;
      }

      let lastRangeKey = "";
      app.ticker.add(() => {
        if (!selectedHex) { rangeGfx.clear(); lastRangeKey = ""; return; }
        selectionGfx.clear();
        const now = Date.now();
        const { x, y } = selectedHex;
        const breathe = 0.6 + 0.2 * Math.sin(now / 600);
        const corners = getHexCorners(x, y, HEX_SIZE + 0.5);
        selectionGfx.poly(corners);
        selectionGfx.fill({ color: 0xffffff, alpha: 0.03 });
        selectionGfx.poly(corners);
        selectionGfx.stroke({ width: 1.5, color: 0xff8c00, alpha: breathe });

        // Range visualization
        const sel = (window as any).__selectedHex;
        if (!sel) { rangeGfx.clear(); lastRangeKey = ""; return; }
        const rangeKey = sel.q + "," + sel.r;
        if (rangeKey === lastRangeKey) return; // Only redraw when selection changes
        lastRangeKey = rangeKey;
        rangeGfx.clear();

        const buildings = sel.buildings || [];
        const playerId = (window as any).__ironpactPlayerId;
        const isOwned = sel.ownerId === playerId;
        if (!isOwned) return;

        // Anti-missile battery range (4 hexes, blue)
        const hasBattery = buildings.some((b: any) => b.type === "antiMissileBattery");
        if (hasBattery) {
          const rangeHexes = hexesInRange(sel.q, sel.r, 4);
          for (const h of rangeHexes) {
            const pos = hexToPixel(h.q, h.r);
            const c = getHexCorners(pos.x, pos.y, HEX_SIZE - 1);
            rangeGfx.poly(c);
            rangeGfx.fill({ color: 0x3388ff, alpha: 0.08 });
            rangeGfx.poly(c);
            rangeGfx.stroke({ width: 1, color: 0x3388ff, alpha: 0.15 });
          }
        }

        // Troop movement range (5 hexes, green)
        if (sel.troops && sel.troops > 0) {
          const moveRange = hexesInRange(sel.q, sel.r, 5);
          for (const h of moveRange) {
            const pos = hexToPixel(h.q, h.r);
            const c = getHexCorners(pos.x, pos.y, HEX_SIZE - 2);
            rangeGfx.poly(c);
            rangeGfx.fill({ color: 0x44ee66, alpha: 0.06 });
            rangeGfx.poly(c);
            rangeGfx.stroke({ width: 1, color: 0x44ee66, alpha: 0.12 });
          }
        }

        // Missile silo range (varies by type, red/orange)
        const hasSilo = buildings.some((b: any) => b.type === "missileSilo");
        if (hasSilo) {
          // Show max range (ICBM=unlimited, so show cruise range=8 as practical visual)
          const siloRange = 8;
          const rangeHexes = hexesInRange(sel.q, sel.r, siloRange);
          for (const h of rangeHexes) {
            const pos = hexToPixel(h.q, h.r);
            const c = getHexCorners(pos.x, pos.y, HEX_SIZE - 2);
            rangeGfx.poly(c);
            rangeGfx.fill({ color: 0xff4444, alpha: 0.04 });
          }
        }

        // Radar station vision range (5 hexes, yellow)
        const hasRadar = buildings.some((b: any) => b.type === "radarStation");
        if (hasRadar) {
          const radarRange = hexesInRange(sel.q, sel.r, 5);
          for (const h of radarRange) {
            const pos = hexToPixel(h.q, h.r);
            const c = getHexCorners(pos.x, pos.y, HEX_SIZE - 1);
            rangeGfx.poly(c);
            rangeGfx.stroke({ width: 1, color: 0xffcc00, alpha: 0.12 });
          }
        }
      });

      // === CAMERA ===
      if (centerOnRef.current) {
        const { x: cx, y: cy } = hexToPixel(centerOnRef.current.q, centerOnRef.current.r);
        mapContainer.scale.set(1.5);
        mapContainer.pivot.set(cx, cy);
        mapContainer.x = app.screen.width / 2;
        mapContainer.y = app.screen.height / 2;
      } else {
        const bounds = mapContainer.getBounds();
        const scaleX = app.screen.width / (bounds.width + 100);
        const scaleY = app.screen.height / (bounds.height + 100);
        mapContainer.scale.set(Math.min(scaleX, scaleY, 1));
        mapContainer.pivot.set(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2);
        mapContainer.x = app.screen.width / 2;
        mapContainer.y = app.screen.height / 2;
      }

      // === INTERACTION (unified touch + mouse) ===
      // Disable Pixi's own event system to avoid conflicts
      app.stage.eventMode = "none";

      const el = containerRef.current!;
      let dragDist = 0;
      let isPinching = false;
      let lastPinchDist = 0;
      let lastPinchCenter = { x: 0, y: 0 };
      const activeTouches = new Map<number, { x: number; y: number }>();

      // Single-finger state
      let pointerDown = false;
      let lastPointer = { x: 0, y: 0 };
      let touchStartTime = 0;
      const TAP_THRESHOLD = 8;  // pixels - actual movement threshold
      const TAP_MAX_TIME = 300; // ms - max time for a tap
      let longPressTimer: any = null;
      let longPressTriggered = false;

      // --- TOUCH HANDLING (mobile) ---
      el.addEventListener("touchstart", (e) => {
        if ((window as any).__ironpactBlockCanvas?.current) return;
        e.preventDefault();
        for (const t of e.changedTouches) {
          activeTouches.set(t.identifier, { x: t.clientX, y: t.clientY });
        }
        if (activeTouches.size === 1) {
          const t = e.changedTouches[0];
          pointerDown = true;
          lastPointer = { x: t.clientX, y: t.clientY };
          dragDist = 0;
          touchStartTime = Date.now();
          isPinching = false;
          longPressTriggered = false;
          const lpX = t.clientX, lpY = t.clientY;
          longPressTimer = setTimeout(() => {
            if (dragDist < TAP_THRESHOLD) {
              longPressTriggered = true;
              const rect = el.getBoundingClientRect();
              const local = mapContainer.toLocal({ x: lpX - rect.left, y: lpY - rect.top });
              const hex = pixelToHex(local.x, local.y);
              const tile = tileMapRef.current.get(`${hex.q},${hex.r}`);
              // Emit long press event via global callback
              if ((window as any).__ironpactLongPress) {
                const globalPt = mapContainer.toGlobal(hexToPixel(hex.q, hex.r));
                (window as any).__ironpactLongPress(hex.q, hex.r, tile, globalPt.x, globalPt.y);
              }
            }
          }, 400);
        } else if (activeTouches.size >= 2) {
          isPinching = true;
          pointerDown = false;
          if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
          const pts = Array.from(activeTouches.values());
          lastPinchDist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          lastPinchCenter = {
            x: (pts[0].x + pts[1].x) / 2,
            y: (pts[0].y + pts[1].y) / 2,
          };
        }
      }, { passive: false });

      el.addEventListener("touchmove", (e) => {
        if ((window as any).__ironpactBlockCanvas?.current) return;
        e.preventDefault();
        for (const t of e.changedTouches) {
          activeTouches.set(t.identifier, { x: t.clientX, y: t.clientY });
        }

        if (activeTouches.size >= 2 && isPinching) {
          // Pinch zoom + two-finger pan
          const pts = Array.from(activeTouches.values());
          const dist = Math.hypot(pts[1].x - pts[0].x, pts[1].y - pts[0].y);
          const center = {
            x: (pts[0].x + pts[1].x) / 2,
            y: (pts[0].y + pts[1].y) / 2,
          };

          if (lastPinchDist > 0) {
            const factor = dist / lastPinchDist;
            const newScale = Math.max(0.15, Math.min(5, mapContainer.scale.x * factor));

            // Zoom toward pinch center
            const before = mapContainer.toLocal({ x: center.x, y: center.y });
            mapContainer.scale.set(newScale);
            const after = mapContainer.toGlobal(before);
            mapContainer.x += center.x - after.x;
            mapContainer.y += center.y - after.y;
          }

          // Pan with two fingers
          mapContainer.x += center.x - lastPinchCenter.x;
          mapContainer.y += center.y - lastPinchCenter.y;

          lastPinchDist = dist;
          lastPinchCenter = center;
        } else if (activeTouches.size === 1 && pointerDown && !isPinching) {
          // Single finger pan
          const t = e.changedTouches[0];
          const dx = t.clientX - lastPointer.x;
          const dy = t.clientY - lastPointer.y;
          mapContainer.x += dx;
          mapContainer.y += dy;
          lastPointer = { x: t.clientX, y: t.clientY };
          dragDist += Math.abs(dx) + Math.abs(dy);
          if (dragDist >= TAP_THRESHOLD && longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
        }
      }, { passive: false });

      el.addEventListener("touchend", (e) => {
        if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; }
        if ((window as any).__ironpactBlockCanvas?.current) { activeTouches.clear(); pointerDown = false; isPinching = false; return; }
        // Handle tap (before removing touches) — skip if long press fired
        if (longPressTriggered) { longPressTriggered = false; for (const t of e.changedTouches) activeTouches.delete(t.identifier); if (activeTouches.size < 2) { isPinching = false; } if (activeTouches.size === 0) pointerDown = false; return; }
        if (activeTouches.size === 1 && !isPinching && dragDist < TAP_THRESHOLD && (Date.now() - touchStartTime) < TAP_MAX_TIME) {
          const t = e.changedTouches[0];
          const rect = el.getBoundingClientRect();
          const px = t.clientX - rect.left;
          const py = t.clientY - rect.top;
          const local = mapContainer.toLocal({ x: px, y: py });
          const hex = pixelToHex(local.x, local.y);
          const { x, y } = hexToPixel(hex.q, hex.r);
          // Toggle: tap same hex = deselect
          if (selectedHex && Math.abs(selectedHex.x - x) < 1 && Math.abs(selectedHex.y - y) < 1) {
            selectedHex = null;
            selectionGfx.clear();
          } else {
            selectedHex = { x, y };
          }
          const tile = tileMapRef.current.get(`${hex.q},${hex.r}`);
          onHexClickRef.current?.(hex.q, hex.r, tile);
        }

        for (const t of e.changedTouches) {
          activeTouches.delete(t.identifier);
        }
        if (activeTouches.size < 2) {
          isPinching = false;
          lastPinchDist = 0;
        }
        if (activeTouches.size === 0) {
          pointerDown = false;
        }
      }, { passive: true });

      // --- MOUSE HANDLING (desktop) ---
      let mouseDown = false;
      let mouseLastPos = { x: 0, y: 0 };
      let mouseDragDist = 0;

      el.addEventListener("mousedown", (e) => {
        if ((window as any).__ironpactBlockCanvas?.current) return;
        mouseDown = true;
        mouseLastPos = { x: e.clientX, y: e.clientY };
        mouseDragDist = 0;
      });

      el.addEventListener("mousemove", (e) => {
        if (!mouseDown) return;
        const dx = e.clientX - mouseLastPos.x;
        const dy = e.clientY - mouseLastPos.y;
        mapContainer.x += dx;
        mapContainer.y += dy;
        mouseLastPos = { x: e.clientX, y: e.clientY };
        mouseDragDist += Math.abs(dx) + Math.abs(dy);
      });

      el.addEventListener("mouseup", (e) => {
        if (mouseDown && mouseDragDist < TAP_THRESHOLD) {
          const rect = el.getBoundingClientRect();
          const px = e.clientX - rect.left;
          const py = e.clientY - rect.top;
          const local = mapContainer.toLocal({ x: px, y: py });
          const hex = pixelToHex(local.x, local.y);
          const { x, y } = hexToPixel(hex.q, hex.r);
          // Toggle: click same hex = deselect
          if (selectedHex && Math.abs(selectedHex.x - x) < 1 && Math.abs(selectedHex.y - y) < 1) {
            selectedHex = null;
            selectionGfx.clear();
          } else {
            selectedHex = { x, y };
          }
          const tile = tileMapRef.current.get(`${hex.q},${hex.r}`);
          onHexClickRef.current?.(hex.q, hex.r, tile);
        }
        mouseDown = false;
      });

      // Wheel zoom (desktop)
      el.addEventListener("wheel", (e) => {
        e.preventDefault();
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        const newScale = Math.max(0.15, Math.min(5, mapContainer.scale.x * factor));
        const rect = el.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const before = mapContainer.toLocal({ x: px, y: py });
        mapContainer.scale.set(newScale);
        const after = mapContainer.toGlobal(before);
        mapContainer.x += px - after.x;
        mapContainer.y += py - after.y;
      }, { passive: false });
    })();

    return () => {
      destroyed = true;
      appRef.current?.destroy(true);
      appRef.current = null;
    };
  }, []);

  return <div ref={containerRef} style={{
    width: "100%",
    height: "calc(100vh - 56px)",
    height: "calc(100dvh - 56px)",
    touchAction: "none",
  } as any} />;
}
